/**
 * CMS SDK - useVersions Hook
 * 版本管理 Hook
 */

import { useState, useCallback } from 'react';
import { useCMS } from '../context/CMSContext';

/**
 * 版本管理 Hook
 * @param {string} contentId - 内容 ID
 * @returns {Object} 版本操作对象
 */
export function useVersions(contentId) {
  const { api } = useCMS();
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [compareVersion, setCompareVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * 加载版本列表
   */
  const load = useCallback(async (id = contentId) => {
    if (!id) return [];
    setLoading(true);
    setError(null);
    try {
      const result = await api.getVersions(id);
      const items = result.items || result.data || result || [];
      setVersions(items);
      return items;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  /**
   * 获取指定版本
   */
  const getVersion = useCallback(async (versionNumber, id = contentId) => {
    if (!id) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getVersion(id, versionNumber);
      setCurrentVersion(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  /**
   * 恢复到指定版本
   */
  const restore = useCallback(async (versionNumber, id = contentId) => {
    if (!id) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await api.restoreVersion(id, versionNumber);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  /**
   * 比较两个版本
   */
  const compare = useCallback((version1, version2) => {
    const v1 = versions.find(v => v.version_number === version1);
    const v2 = versions.find(v => v.version_number === version2);
    if (v1 && v2) {
      setCurrentVersion(v1);
      setCompareVersion(v2);
    }
    return { version1: v1, version2: v2 };
  }, [versions]);

  /**
   * 清除比较
   */
  const clearCompare = useCallback(() => {
    setCompareVersion(null);
  }, []);

  return {
    versions,
    currentVersion,
    compareVersion,
    loading,
    error,
    load,
    getVersion,
    restore,
    compare,
    clearCompare,
    setCurrentVersion,
  };
}

export default useVersions;
